import { Suspense, lazy, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { getSyncEngine, getTaskOutbox } from './local';
import { AuthenticatedTemplate, UnauthenticatedTemplate } from '@azure/msal-react';
import { AppUserProvider, useAppUser } from './auth/AppUserContext';
import { Layout } from './ui/Layout';
import { RequireRole } from './ui/RequireRole';
import { LazyBoundary } from './ui/LazyBoundary';
import { SignInPage } from './pages/SignInPage';
import { HomePage } from './pages/HomePage';
import { NotAuthorizedPage } from './pages/NotAuthorizedPage';
import { AuditsPage } from './pages/audits/AuditsPage';
import { AuditRecordPage } from './pages/audits/AuditRecordPage';
import { ConductAuditPage } from './pages/audits/ConductAuditPage';
import { TasksPage } from './pages/tasks/TasksPage';
import { TaskDetailPage } from './pages/tasks/TaskDetailPage';
import { StoreDetailPage } from './pages/stores/StoreDetailPage';
import { DistrictPage } from './pages/districts/DistrictPage';

/**
 * Online-only, laptop-first areas load on first use (owner approved
 * 2026-10-26): a store phone opening Home no longer downloads the template
 * editor, charts, budgets grid and eight reports. Home, Audits (incl. the
 * conduct form), Tasks, stores and districts stay in the first download —
 * there is no service worker, so a lazy chunk needs the network and the
 * audit form must open in a basement.
 */
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const ComplianceReportPage = lazy(() => import('./pages/reports/ComplianceReportPage').then((m) => ({ default: m.ComplianceReportPage })));
const OpenTasksReportPage = lazy(() => import('./pages/reports/OpenTasksReportPage').then((m) => ({ default: m.OpenTasksReportPage })));
const ScorecardReportPage = lazy(() => import('./pages/reports/ScorecardReportPage').then((m) => ({ default: m.ScorecardReportPage })));
const ActivityLogReportPage = lazy(() => import('./pages/reports/ActivityLogReportPage').then((m) => ({ default: m.ActivityLogReportPage })));
const TaskPerformanceReportPage = lazy(() => import('./pages/reports/TaskPerformanceReportPage').then((m) => ({ default: m.TaskPerformanceReportPage })));
const RemindersReportPage = lazy(() => import('./pages/reports/RemindersReportPage').then((m) => ({ default: m.RemindersReportPage })));
const ConductorsReportPage = lazy(() => import('./pages/reports/ConductorsReportPage').then((m) => ({ default: m.ConductorsReportPage })));
const KpisReportPage = lazy(() => import('./pages/reports/KpisReportPage').then((m) => ({ default: m.KpisReportPage })));
const BudgetsPage = lazy(() => import('./pages/budgets/BudgetsPage').then((m) => ({ default: m.BudgetsPage })));
const AdminShell = lazy(() => import('./pages/admin/AdminShell').then((m) => ({ default: m.AdminShell })));
const StoresAdminPage = lazy(() => import('./pages/admin/StoresAdminPage').then((m) => ({ default: m.StoresAdminPage })));
const UserRolesAdminPage = lazy(() => import('./pages/admin/UserRolesAdminPage').then((m) => ({ default: m.UserRolesAdminPage })));
const OwnersAdminPage = lazy(() => import('./pages/admin/OwnersAdminPage').then((m) => ({ default: m.OwnersAdminPage })));
const DistrictsAdminPage = lazy(() => import('./pages/admin/DistrictsAdminPage').then((m) => ({ default: m.DistrictsAdminPage })));
const NotificationsAdminPage = lazy(() => import('./pages/admin/NotificationsAdminPage').then((m) => ({ default: m.NotificationsAdminPage })));
const TemplatesAdminPage = lazy(() => import('./pages/admin/TemplatesAdminPage').then((m) => ({ default: m.TemplatesAdminPage })));
const TemplateEditorPage = lazy(() => import('./pages/admin/TemplateEditorPage').then((m) => ({ default: m.TemplateEditorPage })));
const ReportsAdminPage = lazy(() => import('./pages/admin/ReportsAdminPage').then((m) => ({ default: m.ReportsAdminPage })));
const ActivityAdminPage = lazy(() => import('./pages/admin/ActivityAdminPage').then((m) => ({ default: m.ActivityAdminPage })));
const CompletionNoticesAdminPage = lazy(() => import('./pages/admin/CompletionNoticesAdminPage').then((m) => ({ default: m.CompletionNoticesAdminPage })));
const AccountPage = lazy(() => import('./pages/account/AccountPage').then((m) => ({ default: m.AccountPage })));
const MyNotificationsPage = lazy(() => import('./pages/notifications/MyNotificationsPage').then((m) => ({ default: m.MyNotificationsPage })));
const AuditPreviewPage = lazy(() => import('./pages/AuditPreviewPage').then((m) => ({ default: m.AuditPreviewPage })));

function AuthenticatedApp() {
  const { user, realUser, loading, unauthorized, error } = useAppUser();
  // The SIGNED-IN account (never "View as"): its saved audits are the only
  // ones this device syncs (shared devices — src/local/ownership.ts).
  const signedInUpn = (realUser ?? user)?.upn ?? null;

  // Background sync runs for the whole signed-in lifetime of the app —
  // drains this account's audit data still queued from earlier sessions too.
  useEffect(() => {
    if (!signedInUpn) return;
    const engine = getSyncEngine();
    engine.setOwner(signedInUpn);
    engine.start();
    // Task comments/files written with no signal (B4) — same owner rule.
    const outbox = getTaskOutbox();
    outbox.setOwner(signedInUpn);
    outbox.start();
    return () => {
      engine.stop();
      outbox.stop();
    };
  }, [signedInUpn]);

  if (loading) {
    return <div className="page-message">Loading your profile…</div>;
  }
  if (error) {
    return (
      <div className="page-message error">
        <p>Could not load your profile: {error}</p>
        <p>
          If this keeps happening, contact IT.
        </p>
        <button className="btn" onClick={() => window.location.reload()}>
          Try again
        </button>
      </div>
    );
  }
  if (unauthorized || !user) {
    return <NotAuthorizedPage />;
  }

  return (
    <Layout user={user}>
      <LazyBoundary>
      <Suspense fallback={<p className="muted" style={{ padding: '1rem' }}>Loading…</p>}>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route
          path="/analytics"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <AnalyticsPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <ReportsPage />
            </RequireRole>
          }
        />
        {/* One route per built report (docs/reports.md); unknown keys fall to the catalogue. */}
        <Route
          path="/reports/compliance"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <ComplianceReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/open-tasks"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <OpenTasksReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/scorecard"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <ScorecardReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/audit-activity"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <ActivityLogReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/task-performance"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <TaskPerformanceReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/reminders"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <RemindersReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/conductors"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <ConductorsReportPage />
            </RequireRole>
          }
        />
        <Route
          path="/reports/kpis"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <KpisReportPage />
            </RequireRole>
          }
        />
        <Route path="/reports/*" element={<Navigate to="/reports" replace />} />
        {/* My account — everyone, from the menu under their name (2026-10-28). */}
        <Route path="/account" element={<AccountPage />} />
        <Route
          path="/notifications"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <MyNotificationsPage />
            </RequireRole>
          }
        />
        <Route
          path="/budgets"
          element={
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'Executive']}>
              <BudgetsPage />
            </RequireRole>
          }
        />
        <Route
          path="/audits"
          element={
            <RequireRole roles={['Auditor', 'DM', 'StoreTeam', 'Admin', 'Executive']}>
              <AuditsPage />
            </RequireRole>
          }
        />
        <Route
          path="/audits/records/:auditId"
          element={
            <RequireRole roles={['Auditor', 'DM', 'StoreTeam', 'Admin', 'Executive']}>
              <AuditRecordPage />
            </RequireRole>
          }
        />
        <Route
          path="/audits/:localId"
          element={
            // Store teams conduct their own self-audits (owner rulings 2026-10-19).
            <RequireRole roles={['Auditor', 'DM', 'Admin', 'StoreTeam']}>
              <ConductAuditPage />
            </RequireRole>
          }
        />
        <Route
          path="/stores/:storeNumber"
          element={
            <RequireRole roles={['Auditor', 'DM', 'StoreTeam', 'Admin', 'Executive']}>
              <StoreDetailPage />
            </RequireRole>
          }
        />
        <Route
          path="/districts/:district"
          element={
            <RequireRole roles={['Auditor', 'DM', 'StoreTeam', 'Admin', 'Executive']}>
              <DistrictPage />
            </RequireRole>
          }
        />
        <Route
          path="/tasks"
          element={
            // Executives read the Tasks hub too, read-only (owner ask 2026-10-21).
            <RequireRole roles={['Auditor', 'DM', 'StoreTeam', 'Admin', 'Executive']}>
              <TasksPage />
            </RequireRole>
          }
        />
        <Route
          path="/tasks/:taskId"
          element={
            // Executives open task links (store pages, grade popovers,
            // emails) read-only: no composer, no manage panel.
            <RequireRole roles={['Auditor', 'DM', 'StoreTeam', 'Admin', 'Executive']}>
              <TaskDetailPage />
            </RequireRole>
          }
        />
        {/* Old bookmark; the preview now lives inside the admin area. */}
        <Route
          path="/audit-preview"
          element={<Navigate to="/admin/preview" replace />}
        />
        <Route
          path="/admin"
          element={
            <RequireRole roles={['Admin']}>
              <AdminShell />
            </RequireRole>
          }
        >
          <Route index element={<Navigate to="/admin/users" replace />} />
          <Route path="users" element={<UserRolesAdminPage />} />
          <Route path="stores" element={<StoresAdminPage />} />
          <Route path="districts" element={<DistrictsAdminPage />} />
          <Route path="owners" element={<OwnersAdminPage />} />
          <Route path="notifications" element={<NotificationsAdminPage />} />
          <Route path="reports" element={<ReportsAdminPage />} />
          <Route path="completion" element={<CompletionNoticesAdminPage />} />
          <Route path="activity" element={<ActivityAdminPage />} />
          <Route path="templates" element={<TemplatesAdminPage />} />
          <Route path="templates/:templateId/edit" element={<TemplateEditorPage />} />
          <Route path="preview" element={<AuditPreviewPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
      </LazyBoundary>
    </Layout>
  );
}

export default function App() {
  return (
    <>
      <UnauthenticatedTemplate>
        <SignInPage />
      </UnauthenticatedTemplate>
      <AuthenticatedTemplate>
        <AppUserProvider>
          <AuthenticatedApp />
        </AppUserProvider>
      </AuthenticatedTemplate>
    </>
  );
}
